/**
 * Playout log: when each interviewer message was actually heard — the TS twin
 * of webui/static/shared/playout.js (same behaviour, same names).
 *
 * The gateway's word timings for an interviewer message (`tts_words`) are
 * relative to the message's own audio. To put them on the recording's clock,
 * finalize needs to know where that audio landed: playback can stall (an
 * underrun) or be cut off by a barge-in. Per delivery of a message this records
 * each place playback started non-contiguously — `[audio_ms, sample]` — the
 * last audio actually played, and the cut.
 *
 * Pure: the page supplies `toFrame(ctxTime)` (context time → page frame, or
 * null) and `toSession(frame)` (page frame → {session, sample} in the live
 * session, or null) — the Recorder's `frameAt` and `toSession`.
 */

/** A chunk starting more than this after the previous one's end opens a new
 *  segment (gapless chunks are scheduled end to end on a cursor). */
export const UNDERRUN_S = 0.02;

export type TtsWord = [string, number, number];

export interface PlayoutEvent {
  type: "playout";
  message_id: string;
  segments: [number, number][];
  end: [number, number];
  cut: number | null;
}

export interface TtsWordsEvent {
  type: "tts_words";
  message_id: string;
  words: TtsWord[];
}

export interface PlayoutResult<S = unknown> {
  session: S;
  event: PlayoutEvent;
  words: TtsWordsEvent | null;
}

export interface PlayoutLogOptions<S = unknown> {
  toFrame: (ctxTime: number) => number | null;
  toSession: (frame: number) => { session: S; sample: number } | null;
  /** Each delivery the log finishes by itself (played out after another
   *  began, idle, or cut) that heard any audio. */
  onFinish?: ((r: PlayoutResult<S>) => void) | null;
  gapS?: number;
}

interface Chunk { at: number; durS: number; audioS: number }
interface Entry { chunks: Chunk[]; words: TtsWord[]; cut: number | null; playing: number; current: boolean }

export class PlayoutLog<S = unknown> {
  private toFrame: PlayoutLogOptions<S>["toFrame"];
  private toSession: PlayoutLogOptions<S>["toSession"];
  private onFinish: ((r: PlayoutResult<S>) => void) | null;
  private gapS: number;
  private open = new Map<string, Entry>();
  private early = new Map<string, TtsWord[]>();   // words that came before begin()
  private current: string | null = null;

  constructor(o: PlayoutLogOptions<S>) {
    this.toFrame = o.toFrame;
    this.toSession = o.toSession;
    this.onFinish = o.onFinish ?? null;
    this.gapS = o.gapS ?? UNDERRUN_S;
  }

  /** A new delivery of `messageId` begins; the delivery before it gets no
   *  more chunks. A repeat of an open id finishes the earlier one first. */
  begin(messageId: string): void {
    if (this.open.has(messageId)) this.auto(messageId);
    if (this.current !== null) this.close(this.current);
    const words = this.early.get(messageId) ?? [];
    this.early.delete(messageId);
    this.open.set(messageId, { chunks: [], words, cut: null, playing: 0, current: true });
    this.current = messageId;
  }

  /** tts word timings, ms into the message's audio. Words for a message not
   *  yet begun wait for it. */
  words(messageId: string, words: TtsWord[]): void {
    if (!messageId || !Array.isArray(words)) return;
    const e = this.open.get(messageId);
    if (e) {
      e.words.push(...words);
      return;
    }
    const early = this.early.get(messageId) ?? [];
    early.push(...words);
    this.early.set(messageId, early);
  }

  /** A chunk of `messageId` starts playing at context time `ctxStart` for
   *  `durationS`. `audioS`: where in the message's audio it starts; default,
   *  straight after the previous chunk. `count`: false for a restart. */
  scheduled(messageId: string, ctxStart: number, durationS: number,
            audioS: number | null = null, count = true): void {
    const e = this.open.get(messageId);
    if (!e) return;
    const last = e.chunks[e.chunks.length - 1];
    const pos = audioS ?? (last ? last.audioS + last.durS : 0);
    e.chunks.push({ at: ctxStart, durS: durationS, audioS: pos });
    if (count) e.playing += 1;
  }

  /** A scheduled chunk of `messageId` played out (or was dropped). */
  ended(messageId: string): void {
    const e = this.open.get(messageId);
    if (!e) return;
    e.playing = Math.max(0, e.playing - 1);
    if (!e.current && e.playing === 0) this.auto(messageId);
  }

  /** The participant cut in at `ctxTime`: nothing of `messageId` (null: of
   *  any open delivery) plays after it. Returns that delivery's result (null
   *  for all, or one that heard nothing). */
  cut(messageId: string | null, ctxTime: number): PlayoutResult<S> | null {
    const ids = messageId === null ? [...this.open.keys()] : [messageId];
    let out: PlayoutResult<S> | null = null;
    for (const id of ids) {
      const e = this.open.get(id);
      if (!e) continue;
      e.cut = ctxTime;
      const r = messageId === null ? this.auto(id) : this.finish(id);
      if (messageId !== null) out = r;
    }
    return out;
  }

  /** No more chunks are coming for the current delivery; with `messageId`,
   *  only if that is the current one. */
  idle(messageId: string | null = null): void {
    if (this.current === null) return;
    if (messageId !== null && messageId !== this.current) return;
    this.close(this.current);
  }

  /** Finish `messageId`, or null when it heard no audio, was cut before its
   *  first chunk, or its audio does not lie in one live session. */
  finish(messageId: string): PlayoutResult<S> | null {
    const e = this.open.get(messageId);
    if (!e) return null;
    this.open.delete(messageId);
    if (this.current === messageId) this.current = null;
    return this.result(messageId, e);
  }

  private close(messageId: string): void {
    const e = this.open.get(messageId);
    if (this.current === messageId) this.current = null;
    if (!e) return;
    e.current = false;
    if (e.playing === 0) this.auto(messageId);
  }

  private auto(messageId: string): PlayoutResult<S> | null {
    const r = this.finish(messageId);
    if (r) this.onFinish?.(r);
    return r;
  }

  private result(messageId: string, e: Entry): PlayoutResult<S> | null {
    let chunks = e.chunks;
    if (e.cut !== null) {
      const at = e.cut;
      chunks = chunks.filter((c) => c.at < at);
    }
    const last = chunks[chunks.length - 1];
    if (!last) return null;
    const segs: [number, number][] = [];
    let prev: Chunk | null = null;
    for (const c of chunks) {
      const resumed = prev !== null && Math.abs(c.audioS - (prev.audioS + prev.durS)) > 1e-6;
      if (!prev || resumed || c.at > prev.at + prev.durS + this.gapS) segs.push([c.audioS, c.at]);
      prev = c;
    }
    let endAudio = last.audioS + last.durS;
    let endAt = last.at + last.durS;
    // A cut after everything had played cut nothing.
    const cut = e.cut !== null && e.cut < endAt ? e.cut : null;
    if (cut !== null) {
      endAudio = last.audioS + (cut - last.at);
      endAt = cut;
    }

    const frames = [...segs.map(([, at]) => this.toFrame(at)), this.toFrame(endAt)];
    if (frames.some((f) => f === null)) return null;
    const mapped = frames.map((f) => this.toSession(f as number));
    const first = mapped[0];
    if (!first || mapped.some((m) => !m || m.session !== first.session)) return null;
    const samples = mapped.map((m) => m!.sample);
    const endSample = samples[samples.length - 1]!;

    const ms = (s: number) => Math.round(s * 1000);
    const event: PlayoutEvent = {
      type: "playout",
      message_id: messageId,
      segments: segs.map(([audioS], k) => [ms(audioS), samples[k]!] as [number, number]),
      end: [ms(endAudio), endSample],
      cut: cut !== null ? endSample : null,
    };
    const words: TtsWordsEvent | null = e.words.length
      ? { type: "tts_words", message_id: messageId, words: e.words }
      : null;
    return { session: first.session, event, words };
  }
}
